// Hydra fee schedule client — series map + currently-active per-event overrides.
//
// Spec: HYDRA_API.md → "Series Fee Schedule".
// `/api/analytics/series/fees` lives on hydra's analytics surface (the only
// hydra REST surface the g1↔hydra relay externalizes with CORS), so it
// shares the same VITE_HYDRA_ANALYTICS_URL + bearer the rest of g1's
// analytics consumption uses (see services/hydraAnalytics.ts).
//
// ⚠️ This used to fetch once per session ("do not poll"). That guidance is
// withdrawn as of 2026-08-07: Kalshi now runs promo discounts at the SERIES
// level and schedules per-EVENT overrides that raise the rate back to full at
// each event's start time (19 MLB series at multiplier 0.5, 363 events
// scheduled to flip to 1.0 at their individual game times). A map fetched at
// 2 PM is wrong by first pitch and stays wrong for the tab's life — every fee
// on a live game reads half the real rate (taker 0.035 vs 0.07), which flows
// straight into displayed edge and arb. So: refresh every 60s, and prefer the
// per-event entry over the series entry on lookup.
//
// `fees` is truthful, not inflated — pre-game MLB genuinely is 0.035 and hydra
// says 0.035. Correctness for live markets comes entirely from `eventFees`.
//
// ⚠️ An empty/absent `eventFees` does NOT reliably mean "no overrides are
// active". When hydra cannot trust its override schedule (upstream failure,
// incomplete sweep, a stale parent series) it SUPPRESSES `eventFees` entirely
// and clamps the affected `fees` entries UP to the undiscounted rate, marking
// them `degraded: true` with `schedule.trusted: false`. Clamped values are safe
// — never understated — but they are not precise. The only correct consumer
// response is to fall back to `fees`, which the lookup below already does. Do
// not infer "no game is live" from an empty map, do not reconstruct overrides
// client-side, and do not merge a suppressed map with a previously-held one.

import { create } from 'zustand';

const BASE = (import.meta.env.VITE_HYDRA_ANALYTICS_URL as string | undefined)?.trim() || '';
const TOKEN = (import.meta.env.VITE_HYDRA_ANALYTICS_TOKEN as string | undefined)?.trim() || '';

// ── HydraCore migration (2026-08-19, Dan-directed) ──────────────────
// The fee schedule is moving off OG hydra's analytics surface onto
// hydracore's plane REST (`/api/plane/v1/series/fees`, byte-compatible
// response incl. the 503 FEES_NOT_READY protocol). The endpoint lives on a
// different surface with a different path shape, so it gets its OWN pin:
//   VITE_HYDRA_FEES_URL    — the FULL endpoint URL (no path is appended)
//   VITE_HYDRA_FEES_TOKEN  — its bearer; defaults to VITE_HYDRA_CORE_TOKEN
//                            (the g1-browser consumer principal, per core)
// Unset ⇒ legacy analytics base + path + analytics bearer, unchanged. The
// analytics pair CANNOT be repointed instead: it serves the whole G3 lane,
// which remains on OG hydra.
const FEES_URL = (import.meta.env.VITE_HYDRA_FEES_URL as string | undefined)?.trim() || '';
const FEES_TOKEN =
  (import.meta.env.VITE_HYDRA_FEES_TOKEN as string | undefined)?.trim() ||
  (import.meta.env.VITE_HYDRA_CORE_TOKEN as string | undefined)?.trim() ||
  '';

export type FeeType = 'quadratic' | 'quadratic_with_maker_fees' | 'flat';

export interface SeriesFeeEntry {
  feeType: FeeType;
  feeMultiplier: number;
  /** Effective taker coefficient (already × multiplier). Plug into × P × (1−P). Undefined for `flat`. */
  feeTakerRate?: number;
  /** Effective maker coefficient. 0 for plain quadratic, ~0.0175 × multiplier for quadratic_with_maker_fees, undefined for flat. */
  feeMakerRate?: number;
  /**
   * `fees` only. True when hydra clamped this series up to the undiscounted
   * standard rate because it couldn't trust the override schedule. Safe (never
   * understated) but less precise — cross-check `schedule.trusted`.
   */
  degraded?: boolean;
}

/** A per-event override that is in effect **right now**. Absent event → use the series entry. */
export interface EventFeeEntry extends SeriesFeeEntry {
  /** ISO timestamp at which the active override took effect. */
  effectiveAt?: string;
}

/** Sweep health for hydra's per-event override schedule. */
export interface FeeScheduleHealth {
  events?: number;
  overrides?: number;
  lastSweepMs?: number;
  lastSweepOk?: boolean;
  everPublished?: boolean;
  /** false → rates are clamped rather than exact. */
  trusted?: boolean;
}

export interface SeriesFeesResponse {
  fees: Record<string, SeriesFeeEntry>;
  /**
   * Optional. Absent until hydra's override-schedule build ships — and also
   * suppressed on purpose whenever hydra can't trust its schedule. Either way
   * the answer is the same: fall back to `fees`. See the header note.
   */
  eventFees?: Record<string, EventFeeEntry>;
  schedule?: FeeScheduleHealth;
  generatedAt: string;
}

/** Diagnostic block on the 503 `FEES_NOT_READY` body. Explains what hydra is still waiting on. */
export interface FeeReadiness {
  series?: boolean;
  schedule?: boolean;
  scheduleTrusted?: boolean;
  staleSeries?: number | string[];
}

// Series prefix = everything from "KX" up to the first hyphen. The class MUST
// include digits: period series carry one (KXNBA1HWINNER, KXNBA1HSPREAD,
// KXNBA1HTOTAL). With a letter-only class the `1` stopped the match before the
// hyphen, so feeFor() returned undefined and every 1H bet rendered fee-free —
// even though hydra's schedule prices them (0.07 taker, same as full game).
const KALSHI_SERIES_PREFIX = /^(KX[A-Z0-9]+?)(?=-)/;

interface FeeState {
  ready: boolean;
  error: string | null;
  fees: Record<string, SeriesFeeEntry>;
  /** Per-event overrides currently in effect. Empty until hydra ships them. */
  eventFees: Record<string, EventFeeEntry>;
  schedule: FeeScheduleHealth | null;
  generatedAt: string | null;
  /** Fetches if needed and arms the 60s refresh loop. Idempotent. */
  loadFees: () => Promise<void>;
  /** Forces a refetch now (used by the refresh loop). */
  refreshFees: () => Promise<void>;
}

let inFlight: Promise<void> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

const BACKGROUND_RETRY_DELAY_MS = 15_000;

/**
 * Overrides activate at each event's start time, so staleness is measured
 * against game clocks, not against hydra's hourly upstream sweep. 60s is the
 * cadence HYDRA_API.md mandates; the payload stays small because `eventFees`
 * carries only currently-active overrides.
 */
export const FEE_REFRESH_INTERVAL_MS = 60_000;

function clearRetryTimer(): void {
  if (!retryTimer) return;
  clearTimeout(retryTimer);
  retryTimer = null;
}

function clearRefreshTimer(): void {
  if (!refreshTimer) return;
  clearTimeout(refreshTimer);
  refreshTimer = null;
}

function scheduleBackgroundRetry(): void {
  if (retryTimer) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    const { ready, loadFees } = useFeeStore.getState();
    if (!ready) {
      loadFees().catch(() => {
        // loadFees records the error and schedules the next retry.
      });
    }
  }, BACKGROUND_RETRY_DELAY_MS);
}

/** Self-rescheduling 60s poll. Only armed once we hold a good map. */
function scheduleRefresh(): void {
  if (refreshTimer) return;
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    useFeeStore
      .getState()
      .refreshFees()
      .catch(() => {
        // A failed refresh keeps the last good map; the loop re-arms below.
      });
  }, FEE_REFRESH_INTERVAL_MS);
}

function runFetch(set: (partial: Partial<FeeState>) => void): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doFetch()
    .then((body) => {
      clearRetryTimer();
      set({
        ready: true,
        fees: body.fees ?? {},
        // Straight REPLACE, never a merge. An absent key means hydra's override
        // build hasn't shipped; an empty one can also mean hydra suppressed the
        // schedule because it couldn't trust it. Both resolve to {} and fall
        // through to `fees` — which is exactly today's behavior, and in the
        // suppressed case `fees` is already clamped up to cover it. Merging in
        // previously-held overrides to "fill the gap" would re-introduce the
        // stale-rate bug this whole change exists to fix.
        eventFees: body.eventFees ?? {},
        schedule: body.schedule ?? null,
        generatedAt: body.generatedAt,
        error: null,
      });
    })
    .catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      // Record the error only — never blank `fees`/`eventFees`. A half-rate
      // map beats no map, and a blank one would silently drop fee adjustment
      // off every row for the length of the outage.
      set({ error: msg });
      if (!useFeeStore.getState().ready) {
        // Cold start: hydra can take several minutes after restart to populate
        // the fee registry, so retry faster than the steady-state cadence
        // instead of stranding the tab until reload.
        scheduleBackgroundRetry();
      }
      throw err;
    })
    .finally(() => {
      inFlight = null;
      // Once we hold a good map, keep the 60s loop running regardless of how
      // this attempt went.
      if (useFeeStore.getState().ready) scheduleRefresh();
    });
  return inFlight;
}

export const useFeeStore = create<FeeState>((set, get) => ({
  ready: false,
  error: null,
  fees: {},
  eventFees: {},
  schedule: null,
  generatedAt: null,
  loadFees: () => {
    if (get().ready) {
      scheduleRefresh();
      return Promise.resolve();
    }
    return runFetch(set);
  },
  refreshFees: () => runFetch(set),
}));

async function doFetch(): Promise<SeriesFeesResponse> {
  if (!FEES_URL && !BASE) {
    throw new Error('VITE_HYDRA_FEES_URL / VITE_HYDRA_ANALYTICS_URL not set — cannot fetch fee schedule');
  }
  const url = FEES_URL || `${BASE.replace(/\/$/, '')}/api/analytics/series/fees`;
  const bearer = FEES_URL ? FEES_TOKEN : TOKEN;
  const headers: HeadersInit = bearer ? { Authorization: `Bearer ${bearer}` } : {};

  // Brief retry on 503 FEES_NOT_READY. The longer cold-start path is handled
  // by scheduleBackgroundRetry() above; this loop just smooths over short
  // readiness races without committing the tab to a long initial await.
  const maxAttempts = 8;
  const delayMs = 400;
  let lastErr: unknown;
  // The 503 body carries a `readiness` block explaining what hydra is still
  // waiting on. Carry the last one into the thrown error so the store's
  // `error` string says *why* fees never arrived rather than just "exhausted".
  let lastReadiness: FeeReadiness | undefined;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const res = await fetch(url, { headers });
      if (res.status === 503) {
        let code: string | undefined;
        try {
          const body = (await res.json()) as { code?: string; readiness?: FeeReadiness };
          code = body?.code;
          if (body?.readiness) lastReadiness = body.readiness;
        } catch {
          // ignore parse failure
        }
        if (code === 'FEES_NOT_READY') {
          await sleep(delayMs);
          continue;
        }
        throw new Error(`hydra fees: 503 ${code ?? ''}${describeReadiness(lastReadiness)}`);
      }
      if (!res.ok) {
        throw new Error(`hydra fees: ${res.status} ${res.statusText}`);
      }
      return (await res.json()) as SeriesFeesResponse;
    } catch (err) {
      lastErr = err;
      if (attempt === maxAttempts - 1) break;
      await sleep(delayMs);
    }
  }
  throw lastErr instanceof Error
    ? lastErr
    : new Error(`hydra fees: exhausted retries${describeReadiness(lastReadiness)}`);
}

/** Renders the 503 readiness block into the error string. Empty when absent (pre-deploy hydra). */
function describeReadiness(r: FeeReadiness | undefined): string {
  if (!r) return '';
  const parts: string[] = [];
  if (r.series !== undefined) parts.push(`series=${r.series}`);
  if (r.schedule !== undefined) parts.push(`schedule=${r.schedule}`);
  if (r.scheduleTrusted !== undefined) parts.push(`scheduleTrusted=${r.scheduleTrusted}`);
  if (r.staleSeries !== undefined) {
    parts.push(`staleSeries=${Array.isArray(r.staleSeries) ? r.staleSeries.join(',') : r.staleSeries}`);
  }
  return parts.length ? ` (${parts.join(' ')})` : '';
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Resolve a market ticker to its fee entry: exact per-event override first,
 * series map second.
 *
 * Deriving the event ticker is NOT a simple last-segment strip. Multi-outcome
 * markets are `{EVENT_TICKER}-{OUTCOME}`
 * (`KXMLBGAME-26AUG071840NYMPIT-NYM`), but single-outcome series like
 * `KXMLBRFI` have no outcome segment at all — there the market ticker IS the
 * event ticker (`KXMLBRFI-26AUG071840NYMPIT`). Validated against 479 live
 * markets: blind stripping missed all 40 KXMLBRFI markets and silently fell
 * back to the discounted series rate. So probe both forms, and require the
 * stripped head to still contain a hyphen (i.e. it's `SERIES-EVENT`, not a
 * bare series) before trusting it as an event key.
 */
function resolveFee(
  ticker: string,
  fees: Record<string, SeriesFeeEntry>,
  eventFees: Record<string, EventFeeEntry>,
): SeriesFeeEntry | undefined {
  if (eventFees[ticker]) return eventFees[ticker]; // market ticker IS the event
  const idx = ticker.lastIndexOf('-');
  const head = idx > 0 ? ticker.slice(0, idx) : '';
  if (head.includes('-') && eventFees[head]) return eventFees[head];
  const m = KALSHI_SERIES_PREFIX.exec(ticker);
  return m ? fees[m[1]] : undefined;
}

/**
 * Sync lookup. Returns undefined for non-Kalshi tickers, unknown series,
 * or before the cache is populated. Use {@link useFeeFor} in React components
 * to re-render when the cache loads.
 */
export function feeFor(ticker: string | null | undefined): SeriesFeeEntry | undefined {
  if (!ticker) return undefined;
  const { fees, eventFees } = useFeeStore.getState();
  return resolveFee(ticker, fees, eventFees);
}

/** React hook that returns the fee entry for a ticker and re-renders when it loads. */
export function useFeeFor(ticker: string | null | undefined): SeriesFeeEntry | undefined {
  const fees = useFeeStore((s) => s.fees);
  const eventFees = useFeeStore((s) => s.eventFees);
  if (!ticker) return undefined;
  return resolveFee(ticker, fees, eventFees);
}

/** React hook for components that just need to know "are fees loaded?". */
export function useFeesReady(): boolean {
  return useFeeStore((s) => s.ready);
}

export type FeeDisplayStatus =
  | 'not-applicable'
  | 'loading'
  | 'missing'
  | 'unsupported'
  | 'estimated'
  | 'exact';

/**
 * Truth state for a fee-adjusted Kalshi quote.
 *
 * A missing or flat schedule must never look like a successful fee-free
 * calculation. `estimated` is different: hydra has deliberately clamped the
 * rate upward, so the quote is conservative and safe to show with a caveat.
 */
export function feeDisplayStatus(
  ticker: string | null | undefined,
  ready: boolean,
  entry: SeriesFeeEntry | undefined,
  schedule: FeeScheduleHealth | null,
): FeeDisplayStatus {
  if (!ticker?.startsWith('KX')) return 'not-applicable';
  if (!ready) return 'loading';
  if (!entry) return 'missing';
  if (entry.feeType === 'flat') return 'unsupported';
  if (entry.degraded || schedule?.trusted === false) return 'estimated';
  return 'exact';
}

/** Reactive companion to {@link feeDisplayStatus}. */
export function useFeeDisplayStatus(
  ticker: string | null | undefined,
  entry: SeriesFeeEntry | undefined,
): FeeDisplayStatus {
  const ready = useFeeStore((s) => s.ready);
  const schedule = useFeeStore((s) => s.schedule);
  return feeDisplayStatus(ticker, ready, entry, schedule);
}

/**
 * Is this fee entry hydra's exact rate, or a safe-but-imprecise clamp?
 *
 * `false` when the entry is flagged `degraded` (hydra clamped the series up to
 * the undiscounted rate because it couldn't trust the override schedule) or
 * when the schedule as a whole reports `trusted: false`. A clamped rate is
 * never *understated*, so trading on it is safe — it just isn't precise, and
 * any surface that presents fees as exact should soften on this.
 *
 * Nothing consumes this yet; it exists so the signal isn't silently dropped on
 * the floor when a UI does want it.
 */
export function feeIsExact(entry: SeriesFeeEntry | undefined): boolean {
  if (!entry) return false;
  if (entry.degraded) return false;
  return useFeeStore.getState().schedule?.trusted !== false;
}

/** React hook exposing hydra's override-schedule sweep health (null pre-deploy). */
export function useFeeSchedule(): FeeScheduleHealth | null {
  return useFeeStore((s) => s.schedule);
}

// ─── Test seam ───────────────────────────────────────────────────────
// Tests can pre-populate the store rather than hit the network.
export function __setFeesForTests(
  fees: Record<string, SeriesFeeEntry>,
  eventFees: Record<string, EventFeeEntry> = {},
): void {
  useFeeStore.setState({
    ready: true,
    fees,
    eventFees,
    generatedAt: new Date().toISOString(),
    error: null,
  });
}

export function __resetFeesForTests(): void {
  inFlight = null;
  clearRetryTimer();
  clearRefreshTimer();
  useFeeStore.setState({
    ready: false,
    fees: {},
    eventFees: {},
    schedule: null,
    generatedAt: null,
    error: null,
  });
}
